import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  addCompDirectSoldToBusiness,
  getCompDirectSoldList,
  updateCompDirectSold,
  deleteCompDirectSold,
} from "../../../../../../apis/Business/CompanyDirectUseOfSoldApi";
import {
  getCompDirectEnergyDropdown,
  getTransportCountryData,
  getCompUseGwpDropdown,
} from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const DirectUseSoldTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownProcess, setDropdownProcess] = useState([]);
  const [dropdownCountry, setDropdownCountry] = useState([]);
  const [dropdownGWP, setDropdownGWP] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getCompDirectSoldList({
      businessId,
      setObjects: setData,
    });
    getCompDirectEnergyDropdown({ setDropdown: setDropdownProcess });
    getTransportCountryData({ setDropdownData: setDropdownCountry });
    getCompUseGwpDropdown({ setDropdown: setDropdownGWP });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0 && dropdownProcess?.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["energy_used.value"]?.value || row.energy_used?.value;

        if (materialValue) {
          const selectedMaterial = dropdownProcess.find(
            (option) =>
              option?.dropdown === materialValue ||
              option?.dropdown_en === materialValue
          );

          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownProcess]);

  const dynamicPlaceholders = useMemo(
    () => ({
      "energy_used.value": {
        targetField: "amount_per_use",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedItem = dropdownData.find(
            (item) =>
              item.dropdown === selectedValue ||
              item.dropdown_en === selectedValue
          );
          if (selectedItem) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedItem.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedItem.unit,
              }));
            }
          }
        },
        dropdownDataKey: "energy_used.value",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      console.log(editedItem);
      updateCompDirectSold({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        energy_used: editedItem["energy_used.value"]
          ? editedItem["energy_used.value"].value
          : editedItem["energy_used"]
          ? editedItem["energy_used"].value
          : null,
        country_sold: editedItem["country_sold.value"]
          ? editedItem["country_sold.value"].value
          : editedItem["country_sold"]
          ? editedItem["country_sold"].value
          : null,
        greenhouse: editedItem["greenhouse.value"]
          ? editedItem["greenhouse.value"].value
          : editedItem["greenhouse"]
          ? editedItem["greenhouse"].value
          : null,
        amount_per_use: editedItem["customMulti_amount_per_use"]
          ? editedItem["customMulti_amount_per_use"]
          : editedItem["amount_per_use"],
        amount_per_lifetime: editedItem["customMulti_amount_per_lifetime"]
          ? editedItem["customMulti_amount_per_lifetime"]
          : editedItem["amount_per_lifetime"],
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, data, setData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addCompDirectSoldToBusiness({
        businessId,
        ...newItem,
        energy_used: newItem["energy_used.value"]
          ? newItem["energy_used.value"].value
          : null,
        country_sold: newItem["country_sold.value"]
          ? newItem["country_sold.value"].value
          : null,
        greenhouse: newItem["greenhouse.value"]
          ? newItem["greenhouse.value"].value
          : null,
        amount_per_use: newItem["customMulti_amount_per_use"],
        amount_per_lifetime: newItem["customMulti_amount_per_lifetime"],
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteCompDirectSold({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "product_name",
        header: t("PRODUCT_NAME"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "100px",
      },
      {
        accessor: "country_sold.value",
        header: `${t("COMP_DIRECT_COUNTRY")}`,
        type: "dropdown",
        dropdownData: dropdownCountry,
        disableSortBy: true,
        width: "130px",
        tipHeader: "Tip",
        tipBody: t("COUNTRY_SOLD_TIP"),
      },
      {
        accessor: "qty_sold",
        header: `${t("COMP_DIRECT_QTY")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        // tipHeader: "Tip",
        // tipBody: t("QTY_SOLD_COUNTRY_TIP"),
      },
      {
        accessor: "life_expectancy",
        header: `${t("COMP_LIFE_EXPECTANCY")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        optional: true,
      },
      {
        accessor: "product_used_times",
        header: `${t("COMP_DIRECT_USED_NUM")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
        isNABtn: true,
        tipHeader: "Tip",
        tipBody: t("PRODUCT_LIFETIME_TIP"),
      },
      {
        accessor: "energy_used.value",
        header: `${t("COMP_ENERGY_DIRECT_USED")}`,
        type: "dropdown",
        dropdownData: dropdownProcess,
        disableSortBy: true,
        width: "150px",
        tipHeader: "Tip",
        tipBody: t("ENERGY_PRODUCT_USE_TIP"),
      },

      {
        accessor: "customMulti",
        header: `${t("COMP_EXPECTED_AMOUNT")}`,
        type: "multipleCustom",
        wide: true,
        layout: "horizontal",
        optional: true,
        width: "300px",
        tipHeader: "Tip",
        tipBody: t("EXPECTED_AMOUNT_TIP"),
        customInputs: [
          {
            accessor: "amount_per_use",
            header: `${t("per use")}`,
            type: "number",
            disableSortBy: true,
            optional: true,
            placeholder: "0",
            width: "60px",
            unit: (rowId) => `${unitMaterials[rowId] || "Unit"}`,
            additionalText: `${t("per use")}`,
            isNA: true,
            accessorNA: "product_used_times",
          },
          {
            accessor: "amount_per_lifetime",
            header: `${t("per lifetime")}`,
            type: "number",
            width: "60px",
            optional: true,
            disableSortBy: true,
            unit: (rowId) => `${unitMaterials[rowId] || "Unit"}`,
            additionalText: `${t("per life")}`,
            isNA: false,
            accessorNA: "product_used_times",
          },
        ],
      },
      {
        accessor: "greenhouse.value",
        header: `${t("COMP_GREENHOUSE")}`,
        type: "dropdown",
        dropdownData: dropdownGWP,
        disableSortBy: true,
        width: "130px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("GREENHOUSE_TIP"),
      },
      {
        accessor: "gas_charged",
        header: `${t("COMP_GWP_NUM")}`,
        type: "number",
        optional: true,
        disableSortBy: true,
        unit: "kg",
      },

      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "90px",
      },
    ],
    [dropdownProcess, dropdownCountry, unitMaterials, dropdownGWP, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "energy_used.value": dropdownProcess,
      "country_sold.value": dropdownCountry,
      "greenhouse.value": dropdownGWP,
    }),
    [dropdownProcess, dropdownCountry, dropdownGWP]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default DirectUseSoldTable;
